import React from 'react';
import classes from './BreadcrumbTreeView.module.scss';
import { ReactComponent as WarningIcon } from 'new/components/icons/fi-rr-warning.svg';
import { ReactComponent as CheckIcon } from 'new/components/icons/check.svg';
import Typography from 'new/elements/TypographySupport';
import type { TreeNode } from './types';

export type PathWarningsProps = {
  showBreadcrumbs: boolean;
  isSelectingFromList: boolean;
  isResolvingPath: boolean;
  isLoadingData: boolean;
  draftOrCommitted: string;
  hasInitialRoot: boolean;
  rootSegValid: boolean;
  lastNavigateFailed: boolean;
  path: TreeNode[];
  currentData: TreeNode[] | TreeNode | undefined;
  onCheckClick: () => void;
};

export const PathWarnings: React.FC<PathWarningsProps> = React.memo(
  ({
    showBreadcrumbs,
    isSelectingFromList,
    isResolvingPath,
    isLoadingData,
    draftOrCommitted,
    hasInitialRoot,
    rootSegValid,
    lastNavigateFailed,
    path,
    currentData,
    onCheckClick,
  }) => {
    const isUserTyping = !isSelectingFromList && !showBreadcrumbs && !!draftOrCommitted;

    const showNotFoundTyping =
      isUserTyping && !!draftOrCommitted && hasInitialRoot && !rootSegValid;

    // During typing: if root is valid but next segment doesn't exist under current folder, show folder not found
    let showDeeperNotFoundTyping = false;
    if (
      isUserTyping &&
      rootSegValid &&
      !isResolvingPath &&
      !isLoadingData &&
      draftOrCommitted.includes('/') &&
      Array.isArray(currentData) &&
      path.length === 1
    ) {
      const segs = draftOrCommitted.split('/').filter((s) => s.length > 0);
      // Only check when typed root matches current root (we're at that root level)
      const typedRoot = (segs[0] || '').toLowerCase();
      const currentRoot = (
        (path[0]?.displayName ||
          (path[0] as any)?.display_name ||
          path[0]?.name ||
          path[0]?.id ||
          '') as string
      ).toLowerCase();
      const isSameRoot =
        typedRoot === currentRoot || typedRoot === String(path[0]?.id || '').toLowerCase();

      if (isSameRoot && segs.length >= 2) {
        const nextSeg = segs[1].toLowerCase();
        const exists = (currentData as TreeNode[]).some((child: any) => {
          const display = (
            child.displayName ||
            (child as any).display_name ||
            child.name ||
            ''
          ).toLowerCase();
          const idLower = String(child.id || '').toLowerCase();
          return display === nextSeg || idLower === nextSeg;
        });
        showDeeperNotFoundTyping = !exists;
      }
    }

    if (showNotFoundTyping) {
      return null;
    }

    return (
      <div className={classes.checkIconContainer}>
        <CheckIcon
          className={classes.checkIcon}
          onClick={onCheckClick}
          data-testid="apply-path-button"
        />
      </div>
    );
  },
);

PathWarnings.displayName = 'PathWarnings';
